import { useFormik } from "formik";
import { FC, useCallback, useEffect } from "react";
import * as yup from "yup";
import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";

import { Dialog } from "components/Dialog";

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (values: FormValuesType) => void;
}

const OVERRIDE_JUSTIFICATION_LENGTH = 3;

const validationSchema = yup.object({
  override_justification: yup
    .string()
    .trim()
    .label("Justification")
    .min(
      OVERRIDE_JUSTIFICATION_LENGTH,
      `Justification must be at least ${OVERRIDE_JUSTIFICATION_LENGTH} characters`
    )
    .required("Justification is required")
});

export interface FormValues {
  override_justification: string;
}

const initialValues: FormValues = {
  override_justification: ""
};

type FormValuesType = typeof initialValues;

export const OverrideAnnualKpiDialog: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm
}): JSX.Element => {
  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, () => {});
    }
  });

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: FormValuesType,
      reset: () => void
    ): Promise<void> => {
      if (!isValid) {
        return;
      }
      onConfirm(values);
      reset();
    },
    [onConfirm]
  );

  const closeAndResetDialog = (): void => {
    onClose();

    setTimeout(() => {
      formik.setErrors({});
      formik.resetForm();
    }, 50);
  };

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.setErrors({});
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const shouldDisableConfirm =
    !formik.isValid ||
    formik.isSubmitting ||
    (!!Object.keys(formik.touched).length && !formik.dirty);

  return (
    <Dialog
      dialogId="override-annualkpi-dialog"
      title="Override KPI"
      open={isOpen}
      size="medium"
      fullWidth={true}
      onClose={closeAndResetDialog}
      data-qa="KPI-Override-AnnualKpi-Dialog"
      confirmDisabled={shouldDisableConfirm}
      confirmText="Save"
      onSubmit={event => {
        event.preventDefault();
        formik.submitForm();
      }}
    >
      <StyledDialogContent>
        <TextField
          variant="outlined"
          fullWidth
          autoFocus
          multiline
          name="override_justification"
          required
          id="override_justification"
          label="Justification"
          data-qa="KPI-Text-Justification"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.override_justification &&
            Boolean(formik.errors.override_justification)
          }
          helperText={
            formik.touched.override_justification &&
            formik.errors.override_justification
          }
          value={formik.values.override_justification}
        />
      </StyledDialogContent>
    </Dialog>
  );
};

const StyledDialogContent = styled("div")`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;
StyledDialogContent.displayName = "StyledDialogContent";
